import { defineStore } from 'pinia'
import { toast } from 'vue3-toastify'
import * as Sentry from '@sentry/browser'
import type { IPaymentTerminal } from '@/interfaces/IPaymentTerminal'
import { paymentTerminalService } from '@/services/payment-terminal.service'
import { useUserStore } from './UserStore'

export interface PaymentTerminalState {
    availableTerminals: IPaymentTerminal[]
    defaultTerminalId: string | null
    isLoading: boolean
}

const storageKey = (userId: string) => `payment_terminal_default_${userId}`

export const usePaymentTerminalStore = defineStore('PaymentTerminalStore', {
    state: (): PaymentTerminalState => ({
        availableTerminals: [],
        defaultTerminalId: null,
        isLoading: false,
    }),

    actions: {
        async fetchAvailable(): Promise<void> {
            this.isLoading = true
            try {
                this.availableTerminals = await paymentTerminalService.getAvailable()
                // Hydrater le TPE par défaut depuis le localStorage
                const userId = useUserStore().me?._id
                if (userId) {
                    const stored = localStorage.getItem(storageKey(userId))
                    if (stored) {
                        // Vérifier que le TPE stocké existe toujours dans la liste
                        const exists = this.availableTerminals.some(t => t._id === stored)
                        this.defaultTerminalId = exists ? stored : null
                        if (!exists && stored) {
                            localStorage.removeItem(storageKey(userId))
                        }
                    }
                }
            } catch (error: any) {
                Sentry.captureException(error)
                toast.error('Impossible de récupérer les terminaux de paiement')
            } finally {
                this.isLoading = false
            }
        },

        setDefault(terminalId: string | null): void {
            const userId = useUserStore().me?._id
            if (!userId) return

            this.defaultTerminalId = terminalId
            if (terminalId) {
                localStorage.setItem(storageKey(userId), terminalId)
            } else {
                localStorage.removeItem(storageKey(userId))
            }
        },

        getDefaultTerminal(): IPaymentTerminal | null {
            if (!this.defaultTerminalId) return null
            return this.availableTerminals.find(t => t._id === this.defaultTerminalId) ?? null
        },
    },

    getters: {
        hasTerminals(): boolean {
            return this.availableTerminals.length > 0
        },
        defaultTerminal(): IPaymentTerminal | null {
            if (!this.defaultTerminalId) return null
            return this.availableTerminals.find(t => t._id === this.defaultTerminalId) ?? null
        },
    },
})
